import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import { useParams } from 'react-router-dom';
import { RcFile } from 'antd/es/upload';
import Button from '../../../../../core/components/button/Button';
import Pagination from '../../../../../core/components/pagination/Pagination';
import { BIG_TABLES_PAGE_SIZES } from '../../../../../core/utils/regex';
import CreateAttachmentModal from '../../../../../common/components/create-attachment/CreateAttachmentModal';
import ConfirmationModal from '../../../../../core/components/confirmation-modal/ConfirmationModal';
import {
  jobAttachmentsFiltersSelector,
  jobAttachmentsSelector,
} from '../../../../../store/selectors/jobsSelector';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { setJobAttachFilters } from '../../../../../store/slices/jobsSlice';
import JobAttachmentsTable from './JobAttachmentsTable';
import {
  createJobAttachmentThunk,
  deleteMultipleJobAttachmentThunk,
  getJobAttachmentsThunk,
} from '../../../../../store/thunks/jobs/jobThunks';
import { setLoading } from '../../../../../store/slices/coreSlice';
import { guidGenerator } from '../../../../../core/utils/guidGenerator';
import { fileAttachmentTypeSelector } from '../../../../../store/selectors/coreSelectors';
import Restricted from '../../../../../permissions-handling/Restricted';
import { PermissionEnum } from '../../../../../core/enums/dictionariesEnums';
import { scrollAttachTable } from '../../../../../common/utils/scrollAttachTable';
import { useSelectAll } from '../../../../../core/hooks/useSelectAll';

const JobAttachments: React.FC = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const attachFilters = useAppSelector(jobAttachmentsFiltersSelector);
  const fileAttachmentType = useAppSelector(fileAttachmentTypeSelector);

  const { items, allOwnItemIds, totalCount } = useAppSelector(jobAttachmentsSelector);
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [comment, setComment] = useState('');
  const [isCreateModal, setCreateModal] = useState(false);
  const [isDeleteModal, setDeleteModal] = useState(false);

  const {
    isAll,
    isIndeterminate,
    checkedKeys,
    checkAllHandle,
    checkRowHandle,
  } = useSelectAll(allOwnItemIds);

  const deleteSelected = () => {
    id && dispatch(deleteMultipleJobAttachmentThunk({
      ids: checkedKeys,
      jobId: +id,
      closeModal: () => setDeleteModal(false),
    }));
  };
  const cancelHandle = () => {
    setCreateModal(false);
    setFileList([]);
    setComment('');
  };

  const uploadHandle = async () => {
    dispatch(setLoading(true));
    const list = fileList.map((el) => ({
      fileName: el.name,
      uploadGuid: guidGenerator(),
      file: el,
    }));
    id && dispatch(createJobAttachmentThunk({
      id: +id,
      list,
      comment,
      onCancel: () => cancelHandle(),
      fileAttachmentType,
    }));
  };

  useEffect(() => {
    id && dispatch(getJobAttachmentsThunk({ id: +id, params: attachFilters }));
    // eslint-disable-next-line
  },[id, attachFilters]);
  return (
    <>
      <CreateAttachmentModal
        isVisible={isCreateModal}
        onCancel={cancelHandle}
        uploadHandle={uploadHandle}
        fileList={fileList}
        setFileList={setFileList}
        comment={comment}
        setComment={setComment}
      />
      <ConfirmationModal
        isVisible={isDeleteModal}
        onCancel={() => {
          setDeleteModal(false);
        }}
        confirmHandler={deleteSelected}
      />
      <div className="attachments">
        <div className="attachments__head actions-and-paging">
          <Restricted to={PermissionEnum.JobEditFields}>
            <div className="attachmentsActions actions-and-paging__actions">
              <Button
                label="Upload file"
                onClick={() => setCreateModal(true)}
                icon={<FontAwesomeIcon icon={faCirclePlus} />}
                designType="dark"
                reversed
              />
              <Button
                label={`Delete selected ${checkedKeys.length ? `(${checkedKeys.length})` : ''}`}
                onClick={() => setDeleteModal(true)}
                disabled={!checkedKeys.length}
              />
            </div>
          </Restricted>
          {totalCount > 0 && <Pagination
            currentPage={attachFilters.page}
            pageSize={attachFilters.pageSize}
            onChange={(page, pageSize) => {
              scrollAttachTable(attachFilters.page, page);
              dispatch(setJobAttachFilters({ ...attachFilters, page, pageSize }));
            }}
            totalItems={totalCount}
            pageSizeOptions={BIG_TABLES_PAGE_SIZES}
            narrow
            className="actions-and-paging__paging"
          />}
        </div>
        <JobAttachmentsTable
          attachments={items}
          checkedKeys={checkedKeys}
          isAll={isAll}
          indeterminate={isIndeterminate}
          checkAllHandle={checkAllHandle}
          checkRow={checkRowHandle}
        />
      </div>
    </>
  );
};

export default JobAttachments;
